
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import React, { useState, useEffect } from 'react';

export const BackButton: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    isTheatreMode, setIsTheatreMode, 
    is3DInspectionActive, setIs3DInspectionActive,
    isMenuOpen, fullscreenGallery, setFullscreenGallery 
  } = useContent();
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const path = location.pathname;
  const isModalOpen = !!(searchParams.get('booking') || searchParams.get('contact'));

  // Logic: 
  // 1. Hide if a modal is open (they have internal close buttons and handle browser back)
  // 2. Always hide if menu is open or on admin/game specific routes
  // 3. Show if we are on the Home page and DO have theatre mode active (to exit it)
  // 4. Show if we are on a sub-page and NOT in theatre mode
  // 5. Show if 3D inspection is active (on project page)
  // 6. Show if fullscreen gallery is active
  const isHome = path === '/';
  const isExcludedRoute = path === '/admin' || path === '/game';
  const isGalleryActive = !!fullscreenGallery;
  
  const shouldShow = !isModalOpen && !isMenuOpen && !isExcludedRoute && (
    (isHome && isTheatreMode) || 
    (!isHome && !isTheatreMode) ||
    is3DInspectionActive ||
    isGalleryActive
  );

  useEffect(() => {
    if (shouldShow) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [shouldShow]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBack = () => {
    if (isGalleryActive) {
      setFullscreenGallery(null);
      return;
    }

    if (is3DInspectionActive) {
      setIs3DInspectionActive(false);
      return;
    }

    if (isTheatreMode) {
      setIsTheatreMode(false);
      document.body.classList.remove('game-active');
      return;
    }

    // Standard navigation logic
    if (path === '/projects') {
      // Return to the specific projects section on home
      navigate("/#projects"); 
    } else if (path.startsWith('/projects/')) {
      navigate("/projects");
    } else if (path === '/services') {
      navigate("/#services");
    } else if (path.startsWith('/services/')) {
      navigate(`/services#service-${params.id}`);
    } else if (path === '/technologies') {
      navigate("/#technologies");
    } else if (path.startsWith('/technologies/')) {
      const techId = params.name?.replace(/\s+/g, '-');
      navigate(`/technologies#tech-${techId}`);
    } else if (path === '/about') {
      navigate("/#about");
    } else if (path === '/team') {
      navigate("/#team");
    } else {
      navigate("/");
    }
  };

  const isExitMode = isTheatreMode || isGalleryActive || is3DInspectionActive;

  // Determine top position based on mode
  const getTopPosition = () => {
    if (isExitMode) return 'top-8';
    return isScrolled ? 'top-28' : 'top-32';
  };

  return (
    <div 
      className={`fixed left-8 z-[2100] transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${getTopPosition()} ${
        isVisible 
          ? 'translate-x-0 opacity-100 scale-100' 
          : '-translate-x-10 opacity-0 scale-50 pointer-events-none'
      }`}
    >
      <button 
        onClick={handleBack}
        className="w-14 h-14 bg-accent text-white rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(249,115,22,0.7)] hover:bg-orange-500 hover:scale-110 active:scale-95 transition-all group border border-white/20 backdrop-blur-md"
        aria-label={isExitMode ? "Close" : "Back"}
      >
        {isExitMode ? (
          <X className="w-7 h-7 group-hover:rotate-90 transition-transform duration-300" strokeWidth={2.5} />
        ) : (
          <ArrowLeft className="w-7 h-7 group-hover:-translate-x-1 transition-transform duration-300" strokeWidth={2.5} />
        )}
      </button>
    </div>
  );
};
